'use client';

import { useState, useMemo } from 'react';
import { CareerPath } from '@/lib/careerPaths';
import { generateLinkedInProfile, LinkedInProfile, HeadlineOption, SkillKeyword } from '@/lib/linkedinGenerator';
import styles from './LinkedInGenerator.module.css';

interface LinkedInGeneratorProps {
  career: CareerPath;
  userSegment?: string | null;
}

export default function LinkedInGenerator({
  career,
  userSegment = null
}: LinkedInGeneratorProps) {
  const [activeSection, setActiveSection] = useState<'headlines' | 'summary' | 'skills' | 'tips'>('headlines');
  const [copiedId, setCopiedId] = useState<string | null>(null);

  // Generate the LinkedIn profile
  const profile: LinkedInProfile = useMemo(() => {
    return generateLinkedInProfile(career, userSegment);
  }, [career, userSegment]);

  const copyToClipboard = async (text: string, id: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const getStyleBadgeClass = (style: HeadlineOption['style']): string => {
    switch (style) {
      case 'aspirational': return styles.styleAspirational;
      case 'hybrid': return styles.styleHybrid;
      case 'achievement': return styles.styleAchievement;
      default: return '';
    }
  };

  const getPriorityClass = (priority: SkillKeyword['priority']): string => {
    switch (priority) {
      case 'must-have': return styles.priorityMustHave;
      case 'recommended': return styles.priorityRecommended;
      case 'nice-to-have': return styles.priorityNiceToHave;
      default: return '';
    }
  };

  const getCategoryIcon = (category: SkillKeyword['category']): string => {
    switch (category) {
      case 'core': return '⚡';
      case 'technical': return '🔧';
      case 'soft': return '🤝';
      case 'industry': return '🏢';
      default: return '•';
    }
  };

  const sections = [
    { id: 'headlines' as const, label: 'Headlines', icon: '✏️' },
    { id: 'summary' as const, label: 'Summary', icon: '📝' },
    { id: 'skills' as const, label: 'Skills', icon: '🎯' },
    { id: 'tips' as const, label: 'Tips', icon: '💡' },
  ];

  return (
    <div className={styles.container}>
      {/* Header */}
      <div className={styles.header}>
        <div className={styles.headerIcon}>💼</div>
        <div className={styles.headerContent}>
          <h3>LinkedIn Profile Optimizer</h3>
          <p>Ready-to-use content for your {career.title} profile</p>
        </div>
      </div>

      {/* Section Tabs */}
      <div className={styles.sectionTabs}>
        {sections.map((section) => (
          <button
            key={section.id}
            className={`${styles.sectionTab} ${activeSection === section.id ? styles.activeSectionTab : ''}`}
            onClick={() => setActiveSection(section.id)}
          >
            <span className={styles.sectionIcon}>{section.icon}</span>
            <span className={styles.sectionLabel}>{section.label}</span>
          </button>
        ))}
      </div>

      {/* Headlines Section */}
      {activeSection === 'headlines' && (
        <div className={styles.sectionContent}>
          <p className={styles.sectionIntro}>
            Your headline is the most searchable part of your profile. Choose one that matches your style:
          </p>

          <div className={styles.headlinesList}>
            {profile.headlines.map((headline, idx) => (
              <div key={idx} className={styles.headlineCard}>
                <div className={styles.headlineHeader}>
                  <span className={`${styles.styleBadge} ${getStyleBadgeClass(headline.style)}`}>
                    {headline.style}
                  </span>
                  <button
                    className={styles.copyButton}
                    onClick={() => copyToClipboard(headline.text, `headline-${idx}`)}
                  >
                    {copiedId === `headline-${idx}` ? '✓ Copied!' : '📋 Copy'}
                  </button>
                </div>
                <p className={styles.headlineText}>{headline.text}</p>
                <p className={styles.headlineDescription}>{headline.description}</p>
              </div>
            ))}
          </div>

          <div className={styles.proTip}>
            <span className={styles.proTipIcon}>💡</span>
            <span>Replace bracketed text [like this] with your specific details before copying.</span>
          </div>
        </div>
      )}

      {/* Summary Section */}
      {activeSection === 'summary' && (
        <div className={styles.sectionContent}>
          <p className={styles.sectionIntro}>
            Your About section should hook readers in the first 3 lines. Here&apos;s a complete template:
          </p>

          {profile.aboutSections.map((section, idx) => (
            <div key={idx} className={styles.summaryBlock}>
              <div className={styles.summaryHeader}>
                <h4>{section.title}</h4>
                <button
                  className={styles.copyButton}
                  onClick={() => copyToClipboard(section.content, `summary-${idx}`)}
                >
                  {copiedId === `summary-${idx}` ? '✓ Copied!' : '📋 Copy'}
                </button>
              </div>
              <div className={styles.summaryContent}>
                {section.content.split('\n').map((line, lineIdx) => (
                  <p key={lineIdx} className={line.startsWith('-') ? styles.bulletPoint : ''}>
                    {line}
                  </p>
                ))}
              </div>
            </div>
          ))}

          {/* Experience Reframes */}
          {profile.experienceReframes.length > 0 && (
            <div className={styles.reframesSection}>
              <h4>Experience Reframing Examples</h4>
              <p className={styles.reframesIntro}>
                Transform your past experience to highlight relevant skills:
              </p>
              {profile.experienceReframes.map((reframe, idx) => (
                <div key={idx} className={styles.reframeCard}>
                  <span className={styles.reframeScenario}>{reframe.scenario}</span>
                  <div className={styles.reframeComparison}>
                    <div className={styles.reframeBefore}>
                      <span className={styles.reframeLabel}>Before:</span>
                      <p>{reframe.original}</p>
                    </div>
                    <div className={styles.reframeArrow}>→</div>
                    <div className={styles.reframeAfter}>
                      <span className={styles.reframeLabel}>After:</span>
                      <p>{reframe.reframed}</p>
                    </div>
                  </div>
                  <p className={styles.reframeWhy}>Why it works: {reframe.whyItWorks}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Skills Section */}
      {activeSection === 'skills' && (
        <div className={styles.sectionContent}>
          <p className={styles.sectionIntro}>
            Add these skills to your profile. Recruiters search by these keywords!
          </p>

          <div className={styles.skillsLegend}>
            <span><span className={styles.priorityMustHave}>●</span> Must-have</span>
            <span><span className={styles.priorityRecommended}>●</span> Recommended</span>
            <span><span className={styles.priorityNiceToHave}>●</span> Nice to have</span>
          </div>

          <div className={styles.skillsGrid}>
            {['must-have', 'recommended', 'nice-to-have'].map(priority => (
              <div key={priority} className={styles.skillPriorityGroup}>
                <h4 className={styles.skillPriorityTitle}>
                  {priority === 'must-have' ? '🔥 Must-Have Skills' :
                   priority === 'recommended' ? '⭐ Recommended' : '✨ Nice to Have'}
                </h4>
                <div className={styles.skillTags}>
                  {profile.skillKeywords
                    .filter(s => s.priority === priority)
                    .map((skill, idx) => (
                      <span
                        key={idx}
                        className={`${styles.skillTag} ${getPriorityClass(skill.priority)}`}
                        onClick={() => copyToClipboard(skill.keyword, `skill-${priority}-${idx}`)}
                        title="Click to copy"
                      >
                        <span className={styles.skillCategoryIcon}>{getCategoryIcon(skill.category)}</span>
                        {skill.keyword}
                        {copiedId === `skill-${priority}-${idx}` && (
                          <span className={styles.copiedIndicator}>✓</span>
                        )}
                      </span>
                    ))}
                </div>
              </div>
            ))}
          </div>

          <button
            className={styles.copyAllButton}
            onClick={() => {
              const allSkills = profile.skillKeywords.map(s => s.keyword).join(', ');
              copyToClipboard(allSkills, 'all-skills');
            }}
          >
            {copiedId === 'all-skills' ? '✓ Copied All!' : '📋 Copy All Skills'}
          </button>
        </div>
      )}

      {/* Tips Section */}
      {activeSection === 'tips' && (
        <div className={styles.sectionContent}>
          <p className={styles.sectionIntro}>
            Optimize every section of your LinkedIn profile:
          </p>

          <div className={styles.tipsList}>
            {profile.tips.map((tip, idx) => (
              <div key={idx} className={`${styles.tipCard} ${tip.impact === 'high' ? styles.highImpact : ''}`}>
                <div className={styles.tipHeader}>
                  <span className={styles.tipSection}>{tip.section}</span>
                  {tip.impact === 'high' && (
                    <span className={styles.highImpactBadge}>High Impact</span>
                  )}
                </div>
                <p className={styles.tipText}>{tip.tip}</p>
              </div>
            ))}
          </div>

          <div className={styles.additionalTips}>
            <h4>Quick Wins (Do Today)</h4>
            <ul>
              <li>Update your headline with your target role</li>
              <li>Add a professional photo</li>
              <li>Enable &quot;Open to Work&quot; for recruiters</li>
              <li>Request 3 skill endorsements from connections</li>
              <li>Follow 10 companies in your target industry</li>
            </ul>
          </div>
        </div>
      )}

      {/* Footer */}
      <div className={styles.footer}>
        <p>
          <strong>Pro Tip:</strong> Update your LinkedIn on a computer, not mobile, for full access to all editing features.
        </p>
      </div>
    </div>
  );
}
